import posthog from 'posthog-js';
import UTMTracker from './UTMTracker';
import { captureLinkCoupon } from './linkCoupon';

/**
 * The one analytics wrapper for the web app. Components import `track`,
 * `identify`, `reset`, `page` (and `trackPurchase` for the GA4 ecommerce
 * purchase) from here and never call an SDK directly.
 *
 * Destinations: PostHog (primary) and GA4 via gtag (secondary). GTM is loaded
 * for whatever tags live in its container. Nothing loads or sends unless this
 * is a production build (REACT_APP_ENV=production); PostHog additionally needs
 * REACT_APP_POSTHOG_KEY. Every call swallows its own errors: tracking must
 * never break a flow.
 *
 * Tracking plan v1 (2026-09-28): surface=web_app, channel=d2c, partner=null.
 */

const IS_PROD = process.env.REACT_APP_ENV === 'production';
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = 'https://us.i.posthog.com';
const PH_ENABLED = IS_PROD && !!POSTHOG_KEY;
const GA_ENABLED = IS_PROD;

const GA4_ID = 'G-F48MNX3TS6';
const ADS_ID = 'AW-10792886696';
// Google Ads conversion "Website Purchase_New" (Purchase goal, values per
// conversion). Fired once per confirmed paid order with the order's margin, so
// bidding can optimise on what an order is worth to Fleek.
const ADS_PURCHASE_SEND_TO = `${ADS_ID}/05BJCMSTu44bEKjDuZoo`;
const GTM_ID = 'GTM-WRDKD7G7';

const USER_ID_KEY = 'fleek_analytics_uid';
const PURCHASES_KEY = 'fleek_ga_purchases';

// Plan event names. Past tense, snake_case, object_action.
export const EVENTS = {
  CATALOGUE_VIEWED: 'catalogue_viewed',
  SEARCH_PERFORMED: 'search_performed',
  BRAND_REQUESTED: 'brand_requested',
  PRODUCT_VIEWED: 'product_viewed',
  OTP_REQUESTED: 'otp_requested',
  OTP_REQUEST_FAILED: 'otp_request_failed',
  OTP_VERIFIED: 'otp_verified',
  OTP_VERIFY_FAILED: 'otp_verify_failed',
  SIGNED_UP: 'signed_up',
  // The sign-in sheet, step by step: shown, a provider button tapped, signed in,
  // or closed without signing in. Outside India the sheet is the only way to
  // checkout, so these are the funnel between product_viewed and checkout_started.
  SIGN_IN_SHOWN: 'sign_in_shown',
  SIGN_IN_STARTED: 'sign_in_started',
  SIGNED_IN: 'signed_in',
  SIGN_IN_DISMISSED: 'sign_in_dismissed',
  MARKET_CHANGED: 'market_changed',
  LOGGED_OUT: 'logged_out',
  CART_ITEM_ADDED: 'cart_item_added',
  // The buy button on a plan sold on the brand's own site. Props: plan_id,
  // plan_name, brand, price, currency. The server records the link it hands
  // out as affiliate_link_opened; this counts the clicks, signed in or not.
  AFFILIATE_CLICKED: 'affiliate_clicked',
  CHECKOUT_STARTED: 'checkout_started',
  COUPON_APPLIED: 'coupon_applied',
  COUPON_REJECTED: 'coupon_rejected',
  COINS_TOGGLED: 'coins_toggled',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_RESULT_VIEWED: 'payment_result_viewed',
  PAYMENT_ABANDONED: 'payment_abandoned',
  ORDER_VIEWED: 'order_viewed',
  CODE_REVEALED: 'code_revealed',
  CODE_COPIED: 'code_copied',
  REDEEM_LINK_OPENED: 'redeem_link_opened',
  ERROR_SHOWN: 'error_shown',
  // Gifting. gift_created, gift_shared and gift_kept carry the Android names.
  GIFT_STARTED: 'gift_started',
  GIFT_CREATED: 'gift_created',
  GIFT_SHARED: 'gift_shared',
  GIFT_KEPT: 'gift_kept',
  // Get-the-app links. Props: store (play|app_store), placement.
  STORE_LINK_CLICKED: 'store_link_clicked',
  // The Android "get the app" bar (components/AppBanner). Props: platform
  // (android), page (home|brand|category|order|gift). Safari's own iOS banner
  // cannot be observed, so it fires nothing.
  APP_BANNER_SHOWN: 'app_banner_shown',
  APP_BANNER_CLICKED: 'app_banner_clicked',
  APP_BANNER_DISMISSED: 'app_banner_dismissed',
  // Bestsellers strip (bestsellers, then Fleek picks).
  // viewed: count, bestseller_count, pick_count.
  // clicked: rank (position in the whole list), kind (bestseller|pick), vendor_id,
  // where (strip|grid_badge; grid_badge is always a bestseller).
  BESTSELLERS_VIEWED: 'bestsellers_viewed',
  BESTSELLER_CLICKED: 'bestseller_clicked',
  TOP_DEALS_VIEWED: 'top_deals_viewed',
  TOP_DEAL_CLICKED: 'top_deal_clicked',
};

// GA4 recommended-event names for the plan events that have one. Everything
// else is mirrored to GA4 under its plan name.
const GA_EVENT_NAMES = {
  [EVENTS.PRODUCT_VIEWED]: 'view_item',
  [EVENTS.CART_ITEM_ADDED]: 'add_to_cart',
  [EVENTS.CHECKOUT_STARTED]: 'begin_checkout',
  [EVENTS.SIGNED_UP]: 'sign_up',
  [EVENTS.OTP_VERIFIED]: 'login',
  [EVENTS.SEARCH_PERFORMED]: 'search',
};
const GA_ECOMMERCE = new Set([EVENTS.PRODUCT_VIEWED, EVENTS.CART_ITEM_ADDED, EVENTS.CHECKOUT_STARTED]);

// Never sent, whatever a caller passes (plan rule 4: no PII, no code values).
const BLOCKED_KEYS = new Set([
  'phone', 'phone_number', 'phoneNo', 'mobile', 'email', 'name', 'user_name',
  'full_name', 'coupon_code', 'code', 'licence_key', 'license_key',
  'voucher_code', 'upi_id', 'vpa', 'card', 'card_number', 'otp',
]);

const safe = (fn) => {
  try {
    return fn();
  } catch (err) {
    if (!IS_PROD) console.warn('[analytics]', err);
    return undefined;
  }
};

// Free text that can echo what the user typed (server error messages).
const SCRUBBED_KEYS = new Set(['reason', 'message']);

// Masks anything shaped like an email or UPI handle (name@okhdfc), and any
// run of 4+ digits (phone numbers, OTPs); then caps the length. Same rule as
// the Android app's scrub().
export const scrub = (text) => {
  if (text === null || text === undefined || text === '') return text;
  return String(text)
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+/g, '<id>')
    .replace(/\d{4,}/g, '<n>')
    .trim()
    .slice(0, 120);
};

const clean = (props = {}) => {
  const out = {};
  Object.keys(props || {}).forEach((k) => {
    if (BLOCKED_KEYS.has(k)) return;
    const v = props[k];
    if (v === undefined) return;
    out[k] = SCRUBBED_KEYS.has(k) && typeof v === 'string' ? scrub(v) : v;
  });
  return out;
};

// The country store this browser shops in (utils/market.js); 'IN' until set.
let marketProperty = 'IN';

const superProperties = () => ({
  surface: 'web_app',
  channel: 'd2c',
  partner: null,
  market: marketProperty,
  app_version: process.env.REACT_APP_VERSION || null,
  env: process.env.REACT_APP_ENV || null,
});

let initialised = false;
let lastPagePath = null;

const gtag = (...args) => {
  if (!GA_ENABLED || typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // gtag.js reads the arguments object itself off the dataLayer, not an array.
  if (!window.gtag) {
    window.gtag = function gtagShim() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  window.gtag(...args);
};

const injectScript = (src) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

const loadGoogle = () => {
  window.dataLayer = window.dataLayer || [];
  // GTM container, for the tags configured in it.
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
  // gtag.js: GA4 + Google Ads. Page views are sent by page(), once per route.
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
  gtag('js', new Date());
  gtag('config', GA4_ID, { send_page_view: false });
  gtag('config', ADS_ID, { send_page_view: false });
};

const registerAttribution = () => {
  const firstTouch = UTMTracker.getFirstTouch();
  if (!firstTouch) return;
  if (PH_ENABLED) posthog.register_once(firstTouch);
  gtag('set', 'user_properties', {
    first_utm_source: firstTouch.utm_source,
    first_utm_medium: firstTouch.utm_medium,
    first_utm_campaign: firstTouch.utm_campaign,
  });
};

/** Once, before first render. Captures UTMs on every landing, dev included. */
export const init = () =>
  safe(() => {
    if (initialised) return;
    initialised = true;

    // Runs everywhere: the latest-touch UTMs also ride on the order payload.
    UTMTracker.captureUTMParams();
    UTMTracker.captureFirstTouch();
    captureLinkCoupon();

    if (PH_ENABLED) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        persistence: 'localStorage+cookie',
      });
      posthog.register(superProperties());
    }
    if (GA_ENABLED) loadGoogle();
    registerAttribution();

    // Already signed in: identify from the id stored at login.
    const stored = localStorage.getItem(USER_ID_KEY);
    if (stored && localStorage.getItem('accesstoken')) identify(stored);
  });

/** One pageview per route change. Call from the router's location effect. */
export const page = (path, search = '') =>
  safe(() => {
    const key = `${path}${search}`;
    if (key === lastPagePath) return;
    lastPagePath = key;
    if (PH_ENABLED) posthog.capture('$pageview', { $current_url: window.location.href, path });
    gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  });

const mirrorToGA = (event, props) => {
  if (!GA_ENABLED) return;
  const params = {};
  Object.keys(props).forEach((k) => {
    const v = props[k];
    if (v === null || ['string', 'number', 'boolean'].includes(typeof v)) params[k] = v;
  });
  if (GA_ECOMMERCE.has(event)) {
    const value = Number(props.amount ?? props.price) || 0;
    params.value = value;
    params.currency = String(props.currency || 'INR').toUpperCase();
    params.items = [{
      item_id: props.plan_id ?? props.bundle_id,
      item_name: props.plan_name,
      item_brand: props.brand,
      price: value,
      quantity: 1,
    }];
  }
  gtag('event', GA_EVENT_NAMES[event] || event, params);
};

/** Plan events only (see EVENTS). */
// Error text for analytics, same rule on every surface: emails and UPI handles
// become <id>, runs of 4+ digits become <n>, capped at 120. Amounts of 4+ digits
// are masked too by design; amounts belong in typed properties.
export const maskErrorText = (value) => {
  if (value == null || value === '') return null;
  const text = typeof value === 'string' ? value : safe(() => JSON.stringify(value)) || String(value);
  return text
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+/g, '<id>')
    .replace(/\d{4,}/g, '<n>')
    .slice(0, 120);
};

/** Every later event carries the store's market as a super property. */
export const setMarketProperty = (market) =>
  safe(() => {
    marketProperty = market || 'IN';
    if (PH_ENABLED && initialised) posthog.register({ market: marketProperty });
  });

export const track = (event, props = {}) =>
  safe(() => {
    const p = clean(props);
    if (PH_ENABLED) posthog.capture(event, p);
    mirrorToGA(event, p);
  });

/**
 * Identify with the backend user id (users.user_id, returned by
 * /users/verify_otp and /users/get_profile). Stored so the next app load can
 * identify without a profile call. Never pass a phone number.
 */
export const identify = (backendUserId) =>
  safe(() => {
    if (!backendUserId) return;
    const raw = String(backendUserId).replace(/^u_/, '');
    localStorage.setItem(USER_ID_KEY, raw);
    const distinctId = `u_${raw}`;
    if (PH_ENABLED && posthog.get_distinct_id() !== distinctId) posthog.identify(distinctId);
    gtag('set', { user_id: distinctId });
  });

/** Is a backend user id already stored for this session? */
export const hasIdentity = () => safe(() => !!localStorage.getItem(USER_ID_KEY)) || false;

/** On logout / account deletion, before storage is cleared. */
export const reset = () =>
  safe(() => {
    localStorage.removeItem(USER_ID_KEY);
    if (PH_ENABLED) {
      posthog.reset();
      posthog.register(superProperties());
    }
    // Attribution belongs to the browser, not the person: keep it.
    registerAttribution();
    gtag('set', { user_id: null });
  });

const readPurchases = () => {
  try {
    return JSON.parse(localStorage.getItem(PURCHASES_KEY) || '[]');
  } catch {
    return [];
  }
};

/**
 * GA4 ecommerce `purchase`, once per order id (persisted, so revisiting the
 * order page never re-fires it). Free and trial plans are skipped, as before.
 * Value is the order margin (plan margin x quantity, never below 0) when the
 * order carries one, else the amount paid.
 */
export const trackPurchase = (order) =>
  safe(() => {
    if (!GA_ENABLED || !order) return;
    const orderId = order.order_id || order.id;
    if (!orderId) return;
    const seen = readPurchases();
    if (seen.includes(String(orderId))) return;

    const items = order.new_ordered_items || [];
    const freeOrTrial = items.some((item) => {
      const n = (item?.subscription_plan?.plan_name || '').toLowerCase();
      return n.includes('free') || n.includes('trial');
    });
    if (freeOrTrial) return;

    const amount = Number(order.payment?.amount || order.total_amount) || 0;
    // `margin` is per unit of the plan, so each line counts quantity times.
    // "0.00" is a real zero; only a missing margin falls back to the amount paid.
    // A bundle sells below the sum of its plans, so its margin is less that discount.
    const margins = items.map((item) => ({
      margin: parseFloat(item?.subscription_plan?.margin),
      cost: parseFloat(item?.subscription_plan?.cost) || 0,
      quantity: Number(item?.quantity) || 1,
    }));
    const hasMargin = margins.some(({ margin }) => Number.isFinite(margin));
    let margin = margins.reduce((sum, { margin: m, quantity }) => sum + (Number.isFinite(m) ? m * quantity : 0), 0);
    const bundleCost = parseFloat(order.bundle?.cost);
    if (Number.isFinite(bundleCost)) {
      const plansCost = margins.reduce((sum, { cost, quantity }) => sum + cost * quantity, 0);
      margin -= Math.max(0, plansCost - bundleCost);
    }
    const currency = String(order.details?.currency_type || order.currency_type || 'INR').toUpperCase();

    const value = hasMargin ? Math.max(0, Math.round(margin * 100) / 100) : amount;
    // Google Ads: one conversion per order (transaction_id dedupes), valued.
    gtag('event', 'conversion', {
      send_to: ADS_PURCHASE_SEND_TO,
      value,
      currency,
      transaction_id: String(orderId),
    });
    gtag('event', 'purchase', {
      transaction_id: String(orderId),
      value,
      currency,
      items: items.map((item) => ({
        item_id: item.subscription_plan?.subscription_plan_id,
        item_name: item.subscription_plan?.plan_name,
        price: Number(item.cost) || 0,
        quantity: Number(item.quantity) || 1,
      })),
    });
    localStorage.setItem(PURCHASES_KEY, JSON.stringify([...seen, String(orderId)].slice(-50)));
  });

const analytics = { init, page, track, identify, hasIdentity, reset, trackPurchase, EVENTS };
export default analytics;
